import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { 
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Stack,
  CircularProgress,
  Tabs,
  Tab,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Chip,
  Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import UndoIcon from '@mui/icons-material/Undo';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import AttachmentIcon from '@mui/icons-material/Attachment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { format, addDays } from 'date-fns';
import Dashboard, { useDashboard } from '../../Layouts/Dashboard';
import InvoiceHeader from './InvoiceHeader'; // Import the invoice header component
import HeaderActions from '../../Components/HeaderActions'; // Import the HeaderActions component
import ClientTable from './Blocks/ClientTable'; // Import our reusable client table component
import { HttpRequestContext } from '../../Blocks/HttpRequestProvider';
import { useParams, useNavigate } from 'react-router-dom';
import PDFViewer from '../../Components/PDFViewer'; // Import the PDFViewer component
import { successToast, errorToast } from '../../Blocks/Toasts'; // Import toast notifications

const InvoiceForm = ({ 
  setHeaderContent: propSetHeaderContent, 
  setHeaderActions: propSetHeaderActions, 
  onInvoiceNumberChange,
  documentType = 'facture' // Default to facture if not specified
}) => {
  // Get the context functions
  const { 
    setHeaderContent: contextSetHeaderContent, 
    setHeaderActions: contextSetHeaderActions 
  } = useDashboard();
  
  // Use the prop version if provided (backward compatibility) or the context version
  const setHeaderContent = propSetHeaderContent || contextSetHeaderContent;
  const setHeaderActions = propSetHeaderActions || contextSetHeaderActions;

  const [viewMode, setViewMode] = useState('edit'); // Default to edit mode for new invoices
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const { sendRequest } = useContext(HttpRequestContext);
  const { id: invoiceId } = useParams(); // Get invoice ID from URL if present
  const navigate = useNavigate(); // Add useNavigate hook
  const isEditMode = !!invoiceId; // Set edit mode if invoice ID exists
  const [showEditConfirmDialog, setShowEditConfirmDialog] = useState(false); // Dialog for edit confirmation
  const [showPdfPreview, setShowPdfPreview] = useState(false); // State for PDF preview dialog
  const [pdfUrl, setPdfUrl] = useState(null); // State for PDF URL
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [selectedSendOption, setSelectedSendOption] = useState(null);
  const [emailFormData, setEmailFormData] = useState({
    to: '',
    cc: [],
    subject: '',
    message: ''
  });
  const [ccInputValue, setCcInputValue] = useState('');

  // Document type display mapping
  const documentTypeDisplay = {
    'facture': 'Facture',
    'recu': 'Reçu'
  };

  // State for invoice data
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: '', // Empty initially, will be filled by API
    invoiceDate: format(new Date(), 'yyyy-MM-dd'),
    dueDate: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    invoiceStatus: 'Brouillon',
    invoice_type: documentType === 'recu' ? 'Receipt' : 'Invoice',
    currency: 'EUR - €',
    client: '',
    clientDetails: {
      name: '',
      companyName: '',
      firstName: '',
      lastName: '',
      fullName: '',
      address: '',
      city: '',
      vatNumber: '',
      bceNumber: '',
      email: '',
      phone: ''
    },
    clientId: '',
    billingProfileId: '',
    providerInformation: null,
    customerInformation: null,
    items: [
      {
        description: '',
        quantity: 1,
        quantityInput: '1',
        price: 0,
        priceInput: '0',
        priceHTVA: 0,
        priceTVAC: 0,
        vat: 21,
        vatInput: '21',
        isPriceTVAC: false,
        total: 0,
        quantityError: false,
        priceError: false,
        vatError: false,
        descriptionError: false
      }
    ],
    subtotal: 0,
    vatAmount: 0,
    totalAmount: 0,
    note: '',
    paymentConditions: '30',
    document_segment: '',
    document_number: null,
    bankDetails: {
      reference: ''
    }
  });

  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    firstname: '',
    lastname: '',
    name: '',
    address: '',
    city: '',
    vatNumber: '',
    bceNumber: '',
    email: '',
    phone: ''
  });
  
  // State for client management
  const [selectedClient, setSelectedClient] = useState(null);

  // Load client selection table columns
  const [clientSelectionColumns, setClientSelectionColumns] = useState(null);
  
  // Add a state to track deleted line items
  const [deletedLineItems, setDeletedLineItems] = useState([]);
  
  useEffect(() => {
    // Try to load saved column settings for the client selection popup
    try {
      const savedColumns = localStorage.getItem('clientSelectionTableColumns');
      if (savedColumns) {
        setClientSelectionColumns(JSON.parse(savedColumns));
      }
    } catch (error) {
      console.error('Error loading saved client selection columns:', error);
    }
  }, []);
  
  // Handle client selection column visibility changes
  const handleColumnVisibilityChange = (updatedColumns) => {
    setClientSelectionColumns(updatedColumns);
    localStorage.setItem('clientSelectionTableColumns', JSON.stringify(updatedColumns));
  };

  // Format a date string for display
  const formatDateForDisplay = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return format(date, 'dd/MM/yyyy');
    } catch (e) {
      return dateStr;
    }
  };

  // Calculate totals
  const calculateTotals = (items) => {
    let subtotal = 0;
    let vatAmount = 0;
    
    items.forEach(item => {
      const quantity = item.quantity || 0;
      const price = item.price || 0;
      const vatRate = item.vat || 0;
      
      if (item.isPriceTVAC) {
        // If price includes VAT, we need to extract the base price
        const priceTVAC = price;
        const priceHTVA = priceTVAC / (1 + (vatRate / 100));
        
        // Calculate item subtotal and VAT amount
        const itemSubtotal = quantity * priceHTVA;
        const itemVat = quantity * (priceTVAC - priceHTVA);
        
        subtotal += itemSubtotal;
        vatAmount += itemVat;
      } else {
        // Standard calculation when price is HTVA
        const itemSubtotal = quantity * price;
        const itemVat = itemSubtotal * (vatRate / 100);
        
        subtotal += itemSubtotal;
        vatAmount += itemVat;
      }
    });
    
    return {
      subtotal: Number(subtotal.toFixed(2)),
      vatAmount: Number(vatAmount.toFixed(2)),
      totalAmount: Number((subtotal + vatAmount).toFixed(2))
    };
  };

  // Handle invoice item change
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...invoiceData.items];
    
    // For checkbox, convert to boolean
    if (field === 'isPriceTVAC') {
      value = Boolean(value);
    }
    
    // For quantity, price, and vat fields, validate but don't modify the display value yet
    if (field === 'quantity' || field === 'price' || field === 'vat') {
      // Store the raw input value for display
      updatedItems[index] = {
        ...updatedItems[index],
        [`${field}Input`]: value,
        [field]: updatedItems[index][field] // Keep the previous valid value for calculations
      };
      
      // Check if the value is a valid number
      const trimmedValue = value.trim();
      if (trimmedValue === '') {
        // Empty input is considered valid (will be treated as 0)
        updatedItems[index][field] = 0;
        updatedItems[index][`${field}Error`] = false;
      } else {
        // Replace comma with dot for parsing
        const normalizedValue = trimmedValue.replace(',', '.');
        const numValue = parseFloat(normalizedValue);
        
        // Check if the entire string is a valid number by comparing the parsed value with the original
        // Use a regex to validate the string format (allows digits, one decimal point, and nothing else)
        const isValidNumberFormat = /^[0-9]*\.?[0-9]+$/.test(normalizedValue);
        
        if (!isNaN(numValue) && isValidNumberFormat) {
          // Only update the actual value if it's a fully valid number
          updatedItems[index][field] = numValue;
          // For VAT field, add additional validation for range (0-100)
          if (field === 'vat' && (numValue < 0 || numValue > 100)) {
            updatedItems[index][`${field}Error`] = true;
          } else {
            // Clear any error if it was previously set
            updatedItems[index][`${field}Error`] = false;
          }
        } else {
          // Set error flag for invalid number format or partial parsing
          updatedItems[index][`${field}Error`] = true;
        }
      }
    } else if (field === 'description') {
      // Handle description validation - check if empty
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
        descriptionError: value.trim() === ''
      };
    } else {
      // For other fields, update directly
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value
      };
    }

    // Recalculate total for this item based on whether price includes VAT or not
    if (field === 'quantity' || field === 'price' || field === 'vat' || field === 'isPriceTVAC') {
      const item = updatedItems[index];
      const quantity = item.quantity || 0;
      const price = item.price || 0;
      const vatRate = item.vat || 0;
      
      if (item.isPriceTVAC) {
        // If price includes VAT, we need to extract the base price first
        const priceTVAC = price;
        const priceHTVA = priceTVAC / (1 + (vatRate / 100));
        
        // The total should be the amount without VAT (quantity * priceHTVA)
        updatedItems[index].total = quantity * priceHTVA;
      } else {
        // If price is HTVA, the total is simply quantity * price
        updatedItems[index].total = quantity * price;
      }
    }

    // Update invoice data and recalculate totals
    const { subtotal, vatAmount, totalAmount } = calculateTotals(updatedItems);
    setInvoiceData({
      ...invoiceData,
      items: updatedItems,
      subtotal,
      vatAmount,
      totalAmount
    });
    setHasUnsavedChanges(true);
  };

  // Add new invoice item
  const addInvoiceItem = () => {
    const newItem = {
      description: '',
      quantity: 1,
      quantityInput: '1',
      price: 0,
      priceInput: '0',
      vat: 20,
      vatInput: '20',
      isPriceTVAC: false,
      total: 0,
      quantityError: false,
      priceError: false,
      vatError: false,
      descriptionError: true
    };

    const updatedItems = [...invoiceData.items, newItem];
    const { subtotal, vatAmount, totalAmount } = calculateTotals(updatedItems);

    setInvoiceData({
      ...invoiceData,
      items: updatedItems,
      subtotal,
      vatAmount,
      totalAmount
    });
    setHasUnsavedChanges(true);
  };

  // Remove invoice item
  const removeInvoiceItem = (index) => {
    if (invoiceData.items.length > 1) {
      // If this is an existing item with an ID, track it for deletion
      const item = invoiceData.items[index];
      if (isEditMode && item.id) {
        setDeletedLineItems(prev => [...prev, item.id]);
      }
      
      const updatedItems = invoiceData.items.filter((_, i) => i !== index);
      const { subtotal, vatAmount, totalAmount } = calculateTotals(updatedItems);

      setInvoiceData({
        ...invoiceData,
        items: updatedItems,
        subtotal,
        vatAmount,
        totalAmount
      });
      setHasUnsavedChanges(true);
    }
  };

  // Handle client selection
  const handleClientSelect = (client) => {
    setSelectedClient(client);
  };

  // Load invoice data when editing an existing invoice or creating a new one
  useEffect(() => {
    if (isEditMode) {
      // Load the invoice data
      loadInvoice();
    } else {
      // For new invoice, fetch invoice number and default billing profile
      fetchInvoiceNumber();
      fetchDefaultBillingProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, documentType]);

  // Function to fetch the default billing profile
  const fetchDefaultBillingProfile = async () => {
    try {
      const response = await sendRequest({
        apiPath: '/invoicing/billingprofile',
        type: 'GET',
      });
      
      if (response) {
        // Extract provider information from billing profile
        const billingProfileId = response._id || response.id;
        
        // Format provider information for the invoice
        const providerInformation = {
          company_name: response.properties?.company_name?.value || '',
          address_line1: response.properties?.address_line1?.value || '',
          address_line2: response.properties?.address_line2?.value || '',
          vat_number: response.properties?.vat_number?.value || '',
          bce_number: response.properties?.bce_number?.value || '',
          iban: response.properties?.iban?.value || '',
          bic: response.properties?.bic?.value || '',
          email: response.properties?.email?.value || ''
        };
        
        // Update invoice data with billing profile information
        setInvoiceData(prevData => ({
          ...prevData,
          billingProfileId,
          providerInformation
        }));
      } else {
        console.error('Failed to fetch default billing profile:', response);
      }
    } catch (err) {
      console.error('Error fetching default billing profile:', err);
    }
  };

  // Function to load invoice data
  const loadInvoice = async () => {
    try {
      setIsLoading(true);
      const response = await sendRequest({
        apiPath: `invoicing/facture/${invoiceId}`,
        type: 'GET'
      });
      
      if (response && response.success) {
        const { invoice, client, billingProfile } = response;
        
        // Format line items to match our UI structure
        const formattedItems = invoice.lineItems ? invoice.lineItems.map(item => ({
          id: item.id,
          description: item.description,
          quantity: item.quantity,
          quantityInput: String(item.quantity),
          price: item.isPriceTVAC ? item.priceTVAC : item.priceHTVA,
          priceInput: String(item.isPriceTVAC ? item.priceTVAC : item.priceHTVA),
          priceHTVA: item.priceHTVA,
          priceTVAC: item.priceTVAC,
          vat: item.vat,
          vatInput: String(item.vat),
          isPriceTVAC: !!item.isPriceTVAC,
          total: item.isPriceTVAC 
            ? item.priceTVAC * item.quantity 
            : item.priceHTVA * item.quantity,
          quantityError: false,
          priceError: false,
          vatError: false
        })) : [];
        
        // Calculate totals
        const { subtotal, vatAmount, totalAmount } = calculateTotals(formattedItems);
        
        // Format client data from the API
        const clientName = client.entity_name || `${client.firstname || ''} ${client.lastname || ''}`.trim();
        const clientDetails = {
          companyName: client.entity_name || '',
          firstName: client.firstname || '',
          lastName: client.lastname || '',
          fullName: `${client.firstname || ''} ${client.lastname || ''}`.trim(),
          name: clientName,
          address: client.address || '',
          city: client.address_line2 || '',
          vatNumber: client.vat_number || '',
          bceNumber: client.bce_number || '',
          email: client.email || '',
          phone: client.phone || ''
        };
        
        // Use formatted dates from the response
        const invoiceDate = invoice.formatted_date_emission || "";
        const dueDate = invoice.formatted_date_echeance || "";

        // Get the invoice status - default to Brouillon if not set
        const invoiceStatus = invoice.status || 'Brouillon';
        
        // Set the initial view mode based on invoice status
        // Only invoices with 'Brouillon' status should start in edit mode
        setViewMode(invoiceStatus === 'Brouillon' ? 'edit' : 'preview');
        
        // Update the invoice data state
        setInvoiceData({
          id: invoice.id,
          invoiceNumber: invoice.num_document,
          invoiceDate: invoiceDate,
          dueDate: dueDate,
          invoiceStatus: invoiceStatus,
          invoice_type: invoice.invoice_type || (documentType === 'recu' ? 'Receipt' : 'Invoice'),
          currency: 'EUR - €', // Default for now
          client: clientName,
          clientDetails,
          clientId: client.id || '',
          billingProfileId: billingProfile.id || '',
          providerInformation: invoice.provider_information || billingProfile,
          customerInformation: invoice.customer_information || client,
          items: formattedItems,
          subtotal,
          vatAmount,
          totalAmount,
          note: invoice.commentaire || '',
          paymentConditions: '30', // Default for now
          document_segment: invoice.document_segment || '',
          document_number: invoice.document_number || null,
          bankDetails: {
            reference: invoice.communication || ''
          }
        });
        
        // Call the callback to update the breadcrumb with the invoice number
        if (onInvoiceNumberChange) {
          onInvoiceNumberChange(invoice.num_document);
        }
      } else {
        console.error('Failed to load invoice:', response);
        alert('Erreur lors du chargement de la facture.');
      }
    } catch (err) {
      console.error('Error loading invoice:', err);
      alert('Erreur lors du chargement de la facture.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle client selection confirmation
  const handleSelectClientConfirm = () => {
    if (selectedClient) {
      // Extract individual name components
      const firstName = selectedClient.firstname || '';
      const lastName = selectedClient.lastname || '';
      const fullName = `${firstName} ${lastName}`.trim();
      const companyName = selectedClient.entity_name || '';
      
      const clientDetails = {
        companyName: companyName,
        firstName: firstName,
        lastName: lastName,
        fullName: fullName, 
        name: companyName || fullName, // For compatibility with existing code
        address: selectedClient.address || '',
        city: selectedClient.address_line2 || '',
        vatNumber: selectedClient.vat_number || '',
        bceNumber: selectedClient.bce_number || '',
        email: selectedClient.email || '',
        phone: selectedClient.phone || ''
      };
      
      setInvoiceData({
        ...invoiceData,
        client: clientDetails.name,
        clientDetails,
        clientId: selectedClient.id || selectedClient._id // Make sure to set the client ID for API calls
      });
      
      setIsClientDialogOpen(false);
    }
  };

  // Handle client creation
  const handleClientCreated = (client) => {
    // Extract individual name components
    const firstName = client.firstname || '';
    const lastName = client.lastname || '';
    const fullName = `${firstName} ${lastName}`.trim();
    const companyName = client.entity_name || '';
    
    // Create client details object with all possible fields
    const clientDetails = {
      companyName: companyName,
      firstName: firstName,
      lastName: lastName,
      fullName: fullName,
      name: companyName || fullName, // For compatibility with existing code
      address: client.address_line1 || '',
      city: client.address_line2 || '',
      vatNumber: client.vat_number || '',
      bceNumber: client.bce_number || '',
      email: client.email || '',
      phone: client.phone || ''
    };
    
    // Update invoice data with new client
    setInvoiceData({
      ...invoiceData,
      client: clientDetails.name,
      clientDetails,
      clientId: client.id || client._id // Make sure to set the client ID for API calls
    });
    
    // Close dialog
    setIsClientDialogOpen(false);
  };

  // Handle client edit
  const handleClientEdit = (client) => {
    // Set the selected client for editing
    setSelectedClient(client);
    // Here you could open an edit form modal if needed
    console.log("Editing client:", client);
    // For demonstration, let's just select this client
    handleClientSelect(client);
  };

  // Handle client delete
  const handleClientDelete = async (clientId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce client ?")) {
      try {
        const response = await sendRequest({
          apiPath: `invoicing/clients/${clientId}`,
          type: 'DELETE'
        });

        if (response && response.success) {
          // If the deleted client was selected, reset selection
          if (selectedClient && selectedClient.id === clientId) {
            setSelectedClient(null);
          }
          // Refresh the client list
          // This will happen automatically as ClientTable refreshes internally
        } else {
          console.error('Failed to delete client:', response);
          alert('Une erreur est survenue lors de la suppression du client.');
        }
      } catch (err) {
        console.error('Error deleting client:', err);
        alert('Une erreur est survenue lors de la suppression du client.');
      }
    }
  };

  // Handle date change
  const handleDateChange = (field, value) => {
    setInvoiceData({
      ...invoiceData,
      [field]: value
    });
    setHasUnsavedChanges(true);
    
    // If invoice date is changed, fetch a new invoice number
    if (field === 'invoiceDate') {
      fetchInvoiceNumber(value);
    }
  };
  
  // Separate the invoice number fetching into a reusable function
  const fetchInvoiceNumber = async (date) => {
    try {
      setIsLoading(true);
      const response = await sendRequest({
        apiPath: `invoicing/document/next-number?document_type=${documentType}${date ? `&date=${date}` : ''}`,
        type: 'GET'
      });
      
      if (response && response.success) {
        const invoiceNumber = response.full_number;
        setInvoiceData(prevData => ({
          ...prevData,
          invoiceNumber,
          document_segment: response.segment,
          document_number: response.next_number
        }));
        // Call the callback to update the breadcrumb
        if (onInvoiceNumberChange) {
          onInvoiceNumberChange(invoiceNumber);
        }
      } else {
        console.error('Failed to fetch next invoice number:', response);
      }
    } catch (err) {
      console.error('Error fetching next invoice number:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle billing profile change
  const handleBillingProfileChange = (billingProfileId) => {
    setInvoiceData({
      ...invoiceData,
      billingProfileId
    });
    setHasUnsavedChanges(true);
  };

  // Handle invoice status change
  const handleStatusChange = (status) => {
    console.log("Setting invoice status to:", status);
    setInvoiceData({
      ...invoiceData,
      invoiceStatus: status
    });
    setHasUnsavedChanges(true);
  };

  // Handle save action
  const handleSave = useCallback(() => {
    // Validate before saving
    const hasEmptyDescriptions = invoiceData.items.some(item => item.description.trim() === '');
    const hasInvalidFields = invoiceData.items.some(item => 
      item.quantityError || item.priceError || item.vatError || item.description.trim() === ''
    );
    
    if (hasInvalidFields) {
      // Mark all empty descriptions as errors
      const updatedItems = invoiceData.items.map(item => ({
        ...item,
        descriptionError: item.description.trim() === ''
      }));
      
      setInvoiceData({
        ...invoiceData,
        items: updatedItems
      });
      
      errorToast('Des champs sont invalides. Veuillez corriger les erreurs avant d\'enregistrer.');
      return;
    }
    
    setIsSaving(true);
    
    // Prepare line items
    const preparedLineItems = invoiceData.items.map(item => {
      // Calculate both prices regardless of which one was entered
      const priceHTVA = !item.isPriceTVAC ? item.price : (item.price / (1 + (item.vat / 100)));
      const priceTVAC = item.isPriceTVAC ? item.price : (item.price * (1 + (item.vat / 100)));
      
      return {
        id: item.id, // Include ID for existing items
        description: item.description,
        quantity: item.quantity,
        priceHTVA: priceHTVA,
        priceTVAC: priceTVAC,
        vat: item.vat,
        isPriceTVAC: item.isPriceTVAC
      };
    });
    
    // Determine if we're creating or updating
    const apiPath = isEditMode 
      ? `invoicing/facture/${invoiceId}`
      : 'invoicing/facture/create';
      
    const requestType = isEditMode ? 'PUT' : 'POST';
    
    // Create the request data
    const requestData = {
      customer: invoiceData.clientId,
      invoiceDate: invoiceData.invoiceDate,
      dueDate: invoiceData.dueDate,
      invoiceStatus: invoiceData.invoiceStatus,
      invoiceNumber: invoiceData.invoiceNumber,
      invoice_type: invoiceData.invoice_type,
      document_segment: invoiceData.document_segment,
      document_number: invoiceData.document_number,
      lineItems: preparedLineItems,
      deletedLineItems: deletedLineItems,
      currency: invoiceData.currency.split(' - ')[0], // Extract the currency code (e.g. "EUR")
      paymentConditions: invoiceData.paymentConditions,
      commentaire: invoiceData.note,
      communication: invoiceData.bankDetails.reference
    };
    
    // Only proceed with API call if invoice has actual status
    if (invoiceData.invoiceStatus) {
      sendRequest({
        apiPath,
        type: requestType,
        request: requestData
      })
      .then(response => {
        setIsSaving(false);
        setHasUnsavedChanges(false);
        
        if (response.success) {
          const message = isEditMode
            ? 'Document mis à jour avec succès!'
            : 'Document enregistré avec succès!';
            
          successToast(message);
          
          // If we received an invoiceId for a new document, redirect to the edit page
          if (response.invoiceId && !isEditMode) {
            console.log('Document saved with ID:', response.invoiceId);
            // Redirect to the edit page for the newly created document
            navigate(`/invoicing/facture/${response.invoiceId}`);
          } else {
            // Just switch to preview mode if we're editing
            setViewMode('preview');
          }
        } else {
          // Error notification
          errorToast(`Erreur: ${response.message || 'Une erreur est survenue lors de l\'opération.'}`);
        }
      })
      .catch(error => {
        setIsSaving(false);
        console.error('Error saving document:', error);
        errorToast('Une erreur est survenue lors de l\'opération.');
      });
    } else {
      // Fallback behavior
      setTimeout(() => {
        setIsSaving(false);
        setHasUnsavedChanges(false);
        setViewMode('preview');
        successToast('Document mis à jour avec succès!');
      }, 1000);
    }
  }, [invoiceData, sendRequest, isEditMode, invoiceId, deletedLineItems, navigate]);

  // Handle print action
  const handlePrint = useCallback(async () => {
    setIsPrinting(true);
    
    try {
      // If we're in edit mode, we use the generate-pdf endpoint
      // If we're in preview mode, we'll use the existing /pdf endpoint (to be implemented later)
      const endpoint = viewMode === 'edit' 
        ? 'invoicing/facture/generate-pdf' 
        : `invoicing/facture/${invoiceId}/pdf`;
      
      // For preview mode, use GET request without sending invoice data
      // For edit mode, use POST and include the full invoice data
      const requestType = viewMode === 'edit' ? 'POST' : 'GET';
      const requestData = viewMode === 'edit' ? {
        // Send the current invoice data
        customer: invoiceData.clientId,
        invoiceDate: invoiceData.invoiceDate,
        dueDate: invoiceData.dueDate,
        invoiceStatus: invoiceData.invoiceStatus,
        invoiceNumber: invoiceData.invoiceNumber,
        invoice_type: invoiceData.invoice_type,
        document_segment: invoiceData.document_segment,
        document_number: invoiceData.document_number,
        lineItems: invoiceData.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          priceHTVA: !item.isPriceTVAC ? item.price : (item.price / (1 + (item.vat / 100))),
          priceTVAC: item.isPriceTVAC ? item.price : (item.price * (1 + (item.vat / 100))),
          vat: item.vat,
          isPriceTVAC: item.isPriceTVAC
        })),
        currency: invoiceData.currency.split(' - ')[0],
        paymentConditions: invoiceData.paymentConditions,
        commentaire: invoiceData.note,
        communication: invoiceData.bankDetails.reference
      } : undefined;
      
      // Generate PDF via API with responseType: 'blob' to handle binary data
      const response = await sendRequest({
        apiPath: endpoint,
        type: requestType,
        responseType: 'blob', // Specify that we're expecting binary data
        request: requestData
      });
      
      if (response) {
        // Create a blob URL directly from the PDF data
        const blob = new Blob([response], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(blob);
        
        setPdfUrl(pdfUrl);
        setShowPdfPreview(true);
      } else {
        console.error('Failed to generate PDF');
        alert('Erreur lors de la génération du PDF.');
      }
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Erreur lors de la génération du PDF.');
    } finally {
      setIsPrinting(false);
    }
  }, [invoiceId, invoiceData, sendRequest, viewMode]);

  // Handle send action
  const handleSend = useCallback(() => {
    // Set the initial email form data based on invoice information and client email
    const clientEmail = invoiceData.clientDetails?.email || '';
    const documentTypeText = invoiceData.invoice_type === 'Receipt' ? 'Reçu' : 'Facture';
    const subject = `${documentTypeText} ${invoiceData.invoiceNumber}`;
    
    // Create a default email message with invoice information
    const message = `Cher client,\n\nVeuillez trouver ci-joint le ${documentTypeText.toLowerCase()} ${invoiceData.invoiceNumber} d'un montant de ${invoiceData.totalAmount.toFixed(2)} € datée du ${formatDateForDisplay(invoiceData.invoiceDate)}.\n\nInformations de paiement:\nIBAN: ${invoiceData.providerInformation?.iban || ''}\nCommunication: ${invoiceData.bankDetails?.reference || ''}\n\nMerci pour votre confiance.\n\nCordialement,`;
    
    setEmailFormData({
      to: clientEmail,
      cc: [],
      subject: subject,
      message: message
    });
    
    // Reset selected option
    setSelectedSendOption(null);
    setShowSendDialog(true);
  }, [invoiceData]);

  // Handle download invoice action
  const handleDownloadInvoice = async () => {
    try {
      // Generate PDF similar to handlePrint but create download instead of preview
      const endpoint = 'invoicing/facture/generate-pdf';
      
      const response = await sendRequest({
        apiPath: endpoint,
        type: 'POST',
        responseType: 'blob',
        request: {
          customer: invoiceData.clientId,
          invoiceDate: invoiceData.invoiceDate,
          dueDate: invoiceData.dueDate,
          invoiceStatus: invoiceData.invoiceStatus,
          invoiceNumber: invoiceData.invoiceNumber,
          invoice_type: invoiceData.invoice_type,
          document_segment: invoiceData.document_segment,
          document_number: invoiceData.document_number,
          lineItems: invoiceData.items.map(item => ({
            description: item.description,
            quantity: item.quantity,
            priceHTVA: !item.isPriceTVAC ? item.price : (item.price / (1 + (item.vat / 100))),
            priceTVAC: item.isPriceTVAC ? item.price : (item.price * (1 + (item.vat / 100))),
            vat: item.vat,
            isPriceTVAC: item.isPriceTVAC
          })),
          currency: invoiceData.currency.split(' - ')[0],
          paymentConditions: invoiceData.paymentConditions,
          commentaire: invoiceData.note,
          communication: invoiceData.bankDetails.reference
        }
      });
      
      if (response) {
        // Create a blob URL from the PDF data
        const blob = new Blob([response], { type: 'application/pdf' });
        const downloadUrl = URL.createObjectURL(blob);
        
        // Create a link element and trigger download
        const link = document.createElement('a');
        link.href = downloadUrl;
        const documentType = invoiceData.invoice_type === 'Receipt' ? 'Recu' : 'Facture';
        link.download = `${documentType}_${invoiceData.invoiceNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Close the send dialog
        setShowSendDialog(false);
      } else {
        console.error('Failed to generate PDF for download');
        alert('Erreur lors de la génération du PDF.');
      }
    } catch (err) {
      console.error('Error generating PDF for download:', err);
      alert('Erreur lors de la génération du PDF pour téléchargement.');
    }
  };

  // Handle send by email action
  const handleSendByEmail = async (savedInvoiceId) => {
    try {
      // Use the saved invoice ID or the current invoice ID
      const invoiceIdToUse = savedInvoiceId || invoiceId;
      
      // Send email without generating PDF (backend will handle attachment)
      const emailResponse = await sendRequest({
        apiPath: 'invoicing/email/send-invoice',
        type: 'POST',
        request: {
          invoiceId: invoiceIdToUse,
          documentType: documentType, // Add document type to the request
          to: emailFormData.to,
          cc: emailFormData.cc.join(','),
          subject: emailFormData.subject,
          message: emailFormData.message
        }
      });
      
      if (emailResponse && emailResponse.success) {
        alert('Email envoyé avec succès!');
        setShowSendDialog(false);
      } else {
        console.error('Failed to send email:', emailResponse);
        alert('Erreur lors de l\'envoi de l\'email.');
      }
    } catch (err) {
      console.error('Error sending email:', err);
      alert('Erreur lors de l\'envoi de l\'email.');
    }
  };

  // Handle email form field change
  const handleEmailFormChange = (field, value) => {
    setEmailFormData({
      ...emailFormData,
      [field]: value
    });
  };

  // Check if email is valid
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Toggle between edit and preview modes
  const toggleViewMode = useCallback(() => {
    // If switching from preview to edit (except for Brouillon status), show confirmation dialog
    if (viewMode === 'preview' && invoiceData.invoiceStatus !== 'Brouillon') {
      setShowEditConfirmDialog(true);
    } else {
      // For Brouillon or switching to preview, just toggle the view mode directly
      setViewMode(viewMode === 'edit' ? 'preview' : 'edit');
    }
  }, [viewMode, invoiceData.invoiceStatus]);

  // Handle confirm edit action
  const handleConfirmEdit = () => {
    setViewMode('edit');
    setShowEditConfirmDialog(false);
  };

  // Handle cancel edit action
  const handleCancelEdit = () => {
    setShowEditConfirmDialog(false);
  };

  // Cancel editing and reload the invoice to discard changes
  const handleCancelChanges = useCallback(() => {
    if (isEditMode) {
      // Reset hasUnsavedChanges flag
      setHasUnsavedChanges(false);
      // Return to preview mode
      setViewMode('preview');
      // Reload the invoice to discard changes
      loadInvoice();
    }
  }, [isEditMode, loadInvoice]);

  // Handle toggle between edit and cancel
  const handleEditCancelToggle = useCallback(() => {
    if (viewMode === 'edit') {
      // In edit mode, this acts as a cancel button
      handleCancelChanges();
    } else {
      // In preview mode, this acts as an edit button
      toggleViewMode();
    }
  }, [viewMode, handleCancelChanges, toggleViewMode]);

  // Set header actions using ClientManagement.js style
  useEffect(() => {
    setHeaderActions(
      <div className="flex gap-2">
        {/* Show Edit/Cancel button for all existing invoices */}
        {isEditMode && (
          <button 
            className={`flex items-center gap-2 ${
              viewMode === 'edit' 
                ? 'bg-red-50 border border-red-300 text-red-600 hover:bg-red-100' 
                : 'bg-white border border-gray-300 hover:bg-gray-50'
            } rounded-md px-4 py-2`}
            onClick={handleEditCancelToggle}
          >
            {viewMode === 'edit' ? <UndoIcon style={{ fontSize: 16 }} /> : <EditIcon style={{ fontSize: 16 }} />}
            <span>{viewMode === 'edit' ? 'Annuler' : 'Modifier'}</span>
          </button>
        )}
        {viewMode === 'edit' && (
          <button 
            className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2 hover:bg-gray-50"
            onClick={handleSave}
          >
            <SaveIcon style={{ fontSize: 16 }} />
            <span>Enregistrer</span>
          </button>
        )}
        <button 
          className="flex items-center gap-2 bg-white border border-gray-300 rounded-md px-4 py-2 hover:bg-gray-50"
          onClick={handlePrint}
        >
          <PrintIcon style={{ fontSize: 16 }} />
          <span>Aperçu PDF</span>
        </button>
        <button 
          className="flex items-center gap-2 bg-blue-600 text-white rounded-md px-4 py-2 hover:bg-blue-700"
          onClick={handleSend}
        >
          <SendIcon style={{ fontSize: 16 }} />
          <span>Envoyer</span>
        </button>
      </div>
    );
    
    // Cleanup function
    return () => setHeaderActions(null);
  }, [setHeaderActions, viewMode, hasUnsavedChanges, handleSave, handlePrint, isEditMode, handleEditCancelToggle, handleSend, invoiceData.invoiceStatus]);

  // Handle option selection
  const handleOptionSelect = (option) => {
    setSelectedSendOption(option);
  };

  // Handle send button click
  const handleSendButtonClick = () => {
    if (!selectedSendOption) {
      alert('Veuillez sélectionner une option d\'envoi');
      return;
    }

    // First set the invoice status to "Envoyée"
    const previousStatus = invoiceData.invoiceStatus;
    
    // Update invoice data with the new status
    setInvoiceData({
      ...invoiceData,
      invoiceStatus: 'Envoyée'
    });
    
    // Save the invoice with the new status
    setIsSaving(true);
    
    // Prepare line items
    const preparedLineItems = invoiceData.items.map(item => {
      // Calculate both prices regardless of which one was entered
      const priceHTVA = !item.isPriceTVAC ? item.price : (item.price / (1 + (item.vat / 100)));
      const priceTVAC = item.isPriceTVAC ? item.price : (item.price * (1 + (item.vat / 100)));
      
      return {
        id: item.id, // Include ID for existing items
        description: item.description,
        quantity: item.quantity,
        priceHTVA: priceHTVA,
        priceTVAC: priceTVAC,
        vat: item.vat,
        isPriceTVAC: item.isPriceTVAC
      };
    });
    
    // Determine if we're creating or updating
    const apiPath = isEditMode 
      ? `invoicing/facture/${invoiceId}`
      : 'invoicing/facture/create';
      
    const requestType = isEditMode ? 'PUT' : 'POST';
    
    // Create the request data with the updated status
    const requestData = {
      customer: invoiceData.clientId,
      invoiceDate: invoiceData.invoiceDate,
      dueDate: invoiceData.dueDate,
      invoiceStatus: 'Envoyée',  // Always set to "Envoyée" when sending
      invoiceNumber: invoiceData.invoiceNumber,
      invoice_type: invoiceData.invoice_type,
      document_segment: invoiceData.document_segment,
      document_number: invoiceData.document_number,
      lineItems: preparedLineItems,
      deletedLineItems: deletedLineItems,
      currency: invoiceData.currency.split(' - ')[0], // Extract the currency code (e.g. "EUR")
      paymentConditions: invoiceData.paymentConditions,
      commentaire: invoiceData.note,
      communication: invoiceData.bankDetails.reference
    };
    
    // Send the save request
    sendRequest({
      apiPath,
      type: requestType,
      request: requestData
    })
    .then(response => {
      setIsSaving(false);
      setHasUnsavedChanges(false);
      
      if (response.success) {
        console.log('Document saved with "Envoyée" status');
        
        // Set view mode to preview after successful save
        setViewMode('preview');
        
        // Now proceed with the selected send option
        if (selectedSendOption === 'download') {
          handleDownloadInvoice();
        } else if (selectedSendOption === 'email') {
          handleSendByEmail(response.invoiceId || invoiceId);
        }
      } else {
        // Error notification
        alert(`Erreur: ${response.message || 'Une erreur est survenue lors de l\'enregistrement du document.'}`);
        
        // Revert the status change if there was an error
        setInvoiceData({
          ...invoiceData,
          invoiceStatus: previousStatus
        });
      }
    })
    .catch(error => {
      setIsSaving(false);
      console.error('Error saving document:', error);
      alert('Une erreur est survenue lors de l\'enregistrement du document.');
      
      // Revert the status change if there was an error
      setInvoiceData({
        ...invoiceData,
        invoiceStatus: previousStatus
      });
    });
  };

  return (
    <>
      {/* 
        Example of using CustomHeaderExample for a completely custom header
        Uncomment the following line to use a fully custom header instead of the default one
        This would replace the default title and breadcrumbs with a custom header layout
      */}
      {/* <CustomHeaderExample setHeaderContent={setHeaderContent} /> */}

      <Box sx={{ p: 3, maxWidth: '1200px', margin: '0 auto' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* No need for top action bar anymore since it's in the header */}

            {/* Invoice Header component */}
            <InvoiceHeader 
              invoiceData={invoiceData}
              setInvoiceData={setInvoiceData}
              viewMode={viewMode}
              setHasUnsavedChanges={setHasUnsavedChanges}
              setIsClientDialogOpen={setIsClientDialogOpen}
              onBillingProfileChange={handleBillingProfileChange}
              onStatusChange={handleStatusChange}
            />

            {/* Edit Confirmation Dialog */}
            <Dialog
              open={showEditConfirmDialog}
              onClose={handleCancelEdit}
            >
              <DialogTitle>Confirmer la Modification</DialogTitle>
              <DialogContent>
                <Typography>
                  Êtes-vous sûr de vouloir modifier ce document ? 
                  Cela peut affecter les documents comptables déjà établis.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCancelEdit}>Annuler</Button>
                <Button onClick={handleConfirmEdit} variant="contained" color="primary">
                  Confirmer
                </Button>
              </DialogActions>
            </Dialog>

            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
              {/* Bank Details Section - Removed as these fields have been relocated */}

              {/* Invoice Items Section */}
              <Typography variant="h6" gutterBottom>
                Articles du Document
              </Typography>

              <TableContainer component={Paper} sx={{ mb: 4 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.light' }}>
                      <TableCell width="40%">Description</TableCell>
                      <TableCell align="center">Quantité</TableCell>
                      <TableCell align="center">Prix</TableCell>
                      <TableCell align="center">TVAC</TableCell>
                      <TableCell align="center">TVA (%)</TableCell>
                      <TableCell align="right">Montant HTVA</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceData.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            fullWidth
                            variant="standard"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            placeholder="Description de l'article"
                            disabled={viewMode === 'preview'}
                            error={item.descriptionError}
                            helperText={item.descriptionError ? "La description est requise" : ""}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box position="relative">
                            <TextField
                              type="text"
                              variant="standard"
                              value={item.quantityInput !== undefined ? item.quantityInput : String(item.quantity)}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              error={item.quantityError}
                              inputProps={{ style: { textAlign: 'center' } }}
                              sx={{ width: '60px' }}
                              disabled={viewMode === 'preview'}
                            />
                            <Fade in={item.quantityError}>
                              <Paper
                                elevation={2}
                                sx={{
                                  position: 'absolute',
                                  top: '-40px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  padding: '4px 8px',
                                  backgroundColor: 'error.light',
                                  color: 'error.contrastText',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  zIndex: 10,
                                  whiteSpace: 'nowrap',
                                  '&:after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: '-4px',
                                    left: '50%',
                                    marginLeft: '-4px',
                                    border: '4px solid transparent',
                                    borderTopColor: (theme) => theme.palette.error.light,
                                  }
                                }}
                              >
                                Format du nombre invalide
                              </Paper>
                            </Fade>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box position="relative">
                            <TextField
                              type="text"
                              variant="standard"
                              value={item.priceInput !== undefined ? item.priceInput : String(item.price)}
                              onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                              error={item.priceError}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">€</InputAdornment>,
                                inputProps: { style: { textAlign: 'center' } }
                              }}
                              sx={{ width: '100px' }}
                              disabled={viewMode === 'preview'}
                            />
                            <Fade in={item.priceError}>
                              <Paper
                                elevation={2}
                                sx={{
                                  position: 'absolute',
                                  top: '-40px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  padding: '4px 8px',
                                  backgroundColor: 'error.light',
                                  color: 'error.contrastText',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  zIndex: 10,
                                  whiteSpace: 'nowrap',
                                  '&:after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: '-4px',
                                    left: '50%',
                                    marginLeft: '-4px',
                                    border: '4px solid transparent',
                                    borderTopColor: (theme) => theme.palette.error.light,
                                  }
                                }}
                              >
                                Format du nombre invalide
                              </Paper>
                            </Fade>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center">
                            <Tooltip title="Prix TVA Comprise">
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={item.isPriceTVAC || false}
                                  onChange={(e) => handleItemChange(index, 'isPriceTVAC', e.target.checked)}
                                  disabled={viewMode === 'preview'}
                                  style={{ marginRight: '4px' }}
                                />
                                <Typography variant="caption">TVAC</Typography>
                              </Box>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box position="relative">
                            <TextField
                              type="text"
                              variant="standard"
                              value={item.vatInput !== undefined ? item.vatInput : String(item.vat)}
                              onChange={(e) => handleItemChange(index, 'vat', e.target.value)}
                              error={item.vatError}
                              inputProps={{ style: { textAlign: 'center' } }}
                              sx={{ width: '60px' }}
                              disabled={viewMode === 'preview'}
                            />
                            <Fade in={item.vatError}>
                              <Paper
                                elevation={2}
                                sx={{
                                  position: 'absolute',
                                  top: '-40px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  padding: '4px 8px',
                                  backgroundColor: 'error.light',
                                  color: 'error.contrastText',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  zIndex: 10,
                                  whiteSpace: 'nowrap',
                                  '&:after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: '-4px',
                                    left: '50%',
                                    marginLeft: '-4px',
                                    border: '4px solid transparent',
                                    borderTopColor: (theme) => theme.palette.error.light,
                                  }
                                }}
                              >
                                Valeur entre 0 et 100
                              </Paper>
                            </Fade>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {item.total.toFixed(2)} €
                        </TableCell>
                        <TableCell align="center">
                          {viewMode === 'edit' && (
                            <IconButton 
                              color="error" 
                              onClick={() => removeInvoiceItem(index)}
                              disabled={invoiceData.items.length <= 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {viewMode === 'edit' && (
                <Box display="flex" justifyContent="center" mb={4}>
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />}
                    onClick={addInvoiceItem}
                  >
                    Ajouter un Article
                  </Button>
                </Box>
              )}

              {/* Notes Section */}
              <Typography variant="h6" gutterBottom>
                Note
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={invoiceData.note}
                onChange={(e) => setInvoiceData({...invoiceData, note: e.target.value})}
                placeholder="Ajoutez une note pour votre client..."
                sx={{ mb: 4 }}
                disabled={viewMode === 'preview'}
              />

              {/* Summary Section */}
              <Typography variant="h6" gutterBottom>
                Récapitulatif
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}></Grid>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" justifyContent="space-between" p={1}>
                      <Typography variant="body1">Sous-total HTVA:</Typography>
                      <Typography variant="body1">{invoiceData.subtotal.toFixed(2)} €</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" p={1}>
                      <Typography variant="body1">TVA:</Typography>
                      <Typography variant="body1">{invoiceData.vatAmount.toFixed(2)} €</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box display="flex" justifyContent="space-between" p={1}>
                      <Typography variant="h6">Total TVAC:</Typography>
                      <Typography variant="h6">{invoiceData.totalAmount.toFixed(2)} €</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Client Selection Dialog */}
            <Dialog open={isClientDialogOpen} onClose={() => setIsClientDialogOpen(false)} maxWidth="lg" fullWidth>
              <DialogContent>
                {/* ClientTable component handles all client management functionality */}
                <ClientTable 
                  selectedClientId={selectedClient?.id}
                  selectionMode={true}
                  onSelectClient={handleClientSelect}
                  onClientCreated={handleClientCreated}
                  handleClientFetching={true}
                  showAddClientModal={true}
                  title="Sélectionner un Client"
                  initialColumns={clientSelectionColumns}
                  onColumnVisibilityChange={handleColumnVisibilityChange}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setIsClientDialogOpen(false)}>Annuler</Button>
                <Button 
                  onClick={handleSelectClientConfirm}
                  variant="contained" 
                  color="primary"
                  disabled={!selectedClient}
                >
                  Sélectionner
                </Button>
              </DialogActions>
            </Dialog>

            {/* PDF Viewer Dialog */}
            <Dialog
              open={showPdfPreview}
              onClose={() => setShowPdfPreview(false)}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: { 
                  minHeight: '90vh',
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  overflow: 'auto'
                }
              }}
              BackdropProps={{
                sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' }
              }}
            >
              <DialogContent 
                sx={{ 
                  padding: 0, 
                  overflow: 'auto',
                  '&:first-of-type': { 
                    paddingTop: 0 
                  }
                }}
              >
                {pdfUrl ? (
                  <PDFViewer pdfUrl={pdfUrl} />
                ) : (
                  <Box display="flex" justifyContent="center" alignItems="center" height="90vh">
                    <CircularProgress />
                  </Box>
                )}
              </DialogContent>
            </Dialog>

            {/* Send Options Dialog */}
            <Dialog 
              open={showSendDialog} 
              onClose={() => setShowSendDialog(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">Options d'envoi</Typography>
                  <IconButton onClick={() => setShowSendDialog(false)}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </DialogTitle>
              <DialogContent dividers>
                <Typography variant="subtitle1" gutterBottom>
                  Comment souhaitez-vous envoyer ce document ?
                </Typography>
                
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {/* Peppol E-Invoice Option - Disabled for now */}
                  <Grid item xs={12} md={4}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        opacity: 0.6,
                        cursor: 'not-allowed'
                      }}
                    >
                      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <ReceiptIcon fontSize="large" color="disabled" />
                            <Chip 
                              label="Indisponible" 
                              size="small" 
                              color="default" 
                              variant="outlined" 
                            />
                          </Box>
                          <Typography variant="h6" sx={{ mt: 2 }}>
                            Peppol (E-Invoice)
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Envoi officiel et non-réversible (Bientôt disponible)
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Download Invoice Option */}
                  <Grid item xs={12} md={4}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        cursor: 'pointer',
                        border: selectedSendOption === 'download' ? '2px solid #1976d2' : '1px solid rgba(0, 0, 0, 0.12)',
                        '&:hover': {
                          boxShadow: 3
                        }
                      }}
                      onClick={() => handleOptionSelect('download')}
                    >
                      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box>
                          <DownloadIcon fontSize="large" color="primary" />
                          <Typography variant="h6" sx={{ mt: 2 }}>
                            Télécharger le Document
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Enregistrer le document en PDF sur votre ordinateur
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  {/* Send by Email Option */}
                  <Grid item xs={12} md={4}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        cursor: 'pointer',
                        border: selectedSendOption === 'email' ? '2px solid #1976d2' : '1px solid rgba(0, 0, 0, 0.12)',
                        '&:hover': {
                          boxShadow: 3
                        }
                      }}
                      onClick={() => handleOptionSelect('email')}
                    >
                      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                        <Box>
                          <EmailIcon fontSize="large" color="primary" />
                          <Typography variant="h6" sx={{ mt: 2 }}>
                            Envoyer par Email
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Envoyer le document par email au client
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                
                {/* Email Form - Only show if email option is selected */}
                {selectedSendOption === 'email' && (
                  <Box mt={3} p={2} border="1px solid #e0e0e0" borderRadius="4px">
                    <Typography variant="h6" gutterBottom>
                      Email
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="À"
                          value={emailFormData.to}
                          onChange={(e) => handleEmailFormChange('to', e.target.value)}
                          placeholder="Adresse email du destinataire"
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Autocomplete
                          multiple
                          freeSolo
                          options={[]}
                          value={emailFormData.cc}
                          onChange={(event, newValue) => {
                            handleEmailFormChange('cc', newValue);
                          }}
                          inputValue={ccInputValue}
                          onInputChange={(event, newInputValue, reason) => {
                            if (reason !== 'reset') {
                              setCcInputValue(newInputValue);
                            }
                          }}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                              const tagProps = getTagProps({ index });
                              const { key, ...chipProps } = tagProps;
                              return (
                                <Chip
                                  key={key}
                                  {...chipProps}
                                  label={option}
                                  color={isValidEmail(option) ? "primary" : "error"}
                                />
                              );
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              label="CC"
                              placeholder="Adresses CC (Appuyez sur Entrée pour ajouter)"
                              size="small"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && ccInputValue) {
                                  // Prevent form submission
                                  e.preventDefault();
                                  
                                  const emailValue = ccInputValue.trim();
                                  if (emailValue) {
                                    // Only add if not already in the list
                                    if (!emailFormData.cc.includes(emailValue)) {
                                      handleEmailFormChange('cc', [...emailFormData.cc, emailValue]);
                                    }
                                    
                                    // Clear the input field
                                    setCcInputValue('');
                                  }
                                }
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Sujet"
                          value={emailFormData.subject}
                          onChange={(e) => handleEmailFormChange('subject', e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Message"
                          value={emailFormData.message}
                          onChange={(e) => handleEmailFormChange('message', e.target.value)}
                          multiline
                          rows={6}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center">
                          <AttachmentIcon fontSize="small" sx={{ mr: 1 }} />
                          <Typography variant="body2">
                            Pièce jointe: Facture_{invoiceData.invoiceNumber}.pdf
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                )}
                
                {/* Big Send Button at the bottom */}
                <Box mt={3} display="flex" justifyContent="center">
                  <Button 
                    variant="contained" 
                    color="primary" 
                    size="large"
                    startIcon={<SendIcon />}
                    onClick={handleSendButtonClick}
                    disabled={!selectedSendOption}
                    sx={{ minWidth: '200px', py: 1.5 }}
                  >
                    {selectedSendOption === 'download' ? 'Télécharger' : 
                     selectedSendOption === 'email' ? 'Envoyer l\'Email' : 'Envoyer'}
                  </Button>
                </Box>
              </DialogContent>
            </Dialog>

            {/* Add/Edit Client Modal - Similar to the one in ClientManagement.js */}
            {/* showAddClientModal and handleAddClient would be defined here */}
          </>
        )}
      </Box>
    </>
  );
};

// Wrap the InvoiceForm component with the Dashboard layout
const FacturePage = () => {
  // Get the invoice data to use in breadcrumbs
  const [currentInvoiceNumber, setCurrentInvoiceNumber] = useState('');
  const { id: invoiceId } = useParams(); // Get invoice ID from URL
  const isEditMode = !!invoiceId; // Are we editing an existing invoice?
  
  // Get the document type from the URL
  const location = window.location.pathname;
  const isReceipt = location.includes('/recu') || location.includes('/receipt');
  const documentType = isReceipt ? 'recu' : 'facture';
  
  // Set the appropriate title based on document type
  const documentTitle = isReceipt ? 'Reçu' : 'Facture';

  // Determine label for new documents with correct gender
  const newDocumentLabel = isReceipt ? `Nouveau ${documentTitle}` : `Nouvelle ${documentTitle}`;
  
  // Dynamic breadcrumbs based on invoice number and document type
  const invoiceBreadcrumbs = [
    { href: '/', label: 'Accueil' },
    { href: '/invoicing', label: 'Facturation' },
    { href: '/invoicing/factures', label: isReceipt ? 'Liste des Reçus' : 'Liste des Factures' },
    { label: isEditMode ? (currentInvoiceNumber || 'Chargement...') : newDocumentLabel }
  ];
  
  // Create a wrapped InvoiceForm that updates the breadcrumbs
  const WrappedInvoiceForm = (props) => {
    return (
      <InvoiceForm 
        {...props}
        documentType={documentType}
        onInvoiceNumberChange={(invoiceNumber) => setCurrentInvoiceNumber(invoiceNumber)} 
      />
    );
  };
  
  return (
    <Dashboard 
      dashboardTitle={isEditMode ? `${documentTitle} ${currentInvoiceNumber}` : newDocumentLabel} 
      breadcrumbs={invoiceBreadcrumbs}
    >
      <WrappedInvoiceForm />
    </Dashboard>
  );
};

export default FacturePage;